import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterResourceType, setFilterResourceType] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const logsPerPage = 50;


  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: currentPage * logsPerPage,
        limit: logsPerPage
      });
      if (filterAction) params.append('action', filterAction);
      if (filterResourceType) params.append('resource_type', filterResourceType);
      const response = await axiosClient.get(`/audit-logs/?${params.toString()}`);
      setLogs(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, logsPerPage, filterAction, filterResourceType]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosClient.get('/audit-logs/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return '#28a745';
      case 'update': return '#ffc107';
      case 'delete': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getResourceTypeLabel = (type) => {
    const labels = {
      'cert_body': 'Certification Body',
      'client': 'Client Company',
      'user': 'User Account'
    };
    return labels[type] || type;
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '10px', color: '#333' }}>Security Audit Logs</h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Track all administrative actions and security events
      </p>

      {/* Statistics Cards */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '30px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            borderRadius: '8px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Events</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '5px' }}>
              {stats.total_logs}
            </div>
          </div>
          
          {Object.entries(stats.actions).map(([action, count]) => (
            <div key={action} style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '2px solid #e9ecef'
            }}>
              <div style={{ fontSize: '14px', color: '#666', textTransform: 'capitalize' }}>
                {action} Actions
              </div>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                marginTop: '5px',
                color: getActionColor(action)
              }}>
                {count}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #dee2e6',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
            Filter by Action
          </label>
          <select
            value={filterAction}
            onChange={(e) => { setFilterAction(e.target.value); setCurrentPage(0); }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
            Filter by Resource
          </label>
          <select
            value={filterResourceType}
            onChange={(e) => { setFilterResourceType(e.target.value); setCurrentPage(0); }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="">All Resources</option>
            <option value="cert_body">Certification Bodies</option>
            <option value="client">Client Companies</option>
            <option value="user">User Accounts</option>
          </select>
        </div>

        <button
          onClick={() => {
            setFilterAction('');
            setFilterResourceType('');
            setCurrentPage(0);
          }}
          style={{
            padding: '8px 16px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            marginTop: '20px'
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Logs Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading audit logs...
        </div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No audit logs found
        </div>
      ) : (
        <>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #dee2e6'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    Timestamp
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    User
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    Action
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    Resource Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    Resource ID
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                      {formatDate(log.created_at)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                      <div style={{ fontWeight: '500' }}>{log.username}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {log.user_id}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: getActionColor(log.action) + '20',
                        color: getActionColor(log.action),
                        textTransform: 'capitalize'
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                      {getResourceTypeLabel(log.resource_type)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#6c757d' }}>
                      #{log.resource_id}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                      {log.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginTop: '20px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              style={{
                padding: '8px 16px',
                background: currentPage === 0 ? '#e9ecef' : '#007bff',
                color: currentPage === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            
            <span style={{ color: '#666', fontSize: '14px' }}>
              Page {currentPage + 1}
            </span>
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={logs.length < logsPerPage}
              style={{
                padding: '8px 16px',
                background: logs.length < logsPerPage ? '#e9ecef' : '#007bff',
                color: logs.length < logsPerPage ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: logs.length < logsPerPage ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AuditLogs;
